import { parseDocumentFromString } from 'anodum';
import site from '../../site';
import hooks from '../../hooks';
import blocks from '../../blocks';
import view from '../../view';
import constants from '../constants';
import verifiers from '../verifiers';
import helpers from '../helpers';
import blocksConstants from '../../blocks/constants/common';
import actionTypes from '../constants/action-types';
import { UPDATE_STATIC_ONLY } from '../../hdrm/constants/update-strategies';
import template from '../../template';

const { isCustomColorUsedOnThePage, isCssColorVariableExist, isCustomColorUsed } = verifiers;
const {
  cleanUpColorFromStylesheet,
  updateCustomColorStylesheetInGlobal,
  getCustomTextColorsStylesheet,
  removeBlockIdRuleFromStylesheet,
  removeBlockIdPropertyFromStylesheet,
  getTextColorsUsedByBlock,
  getElementCustomColorVariables,
} = helpers;

const { BLOCK_ID_PREFIX } = blocksConstants;
const { TEXT_COLOR_OPTIONS_VARIABLE } = constants;

const BLOCK_SELECTOR = `[id^="${BLOCK_ID_PREFIX}"]`;

const getBlocksList = (doc) => doc.querySelectorAll(BLOCK_SELECTOR);

const cleanUpUsedColors = (state, pageId, shouldCheckUsageOnThePage = true) => {
  const usedCustomColors = site.selectors.getUsedCustomColors(state, pageId);

  usedCustomColors.forEach((usedCustomColor) => {
    if (
      !shouldCheckUsageOnThePage ||
      (shouldCheckUsageOnThePage && !isCustomColorUsedOnThePage(usedCustomColor))
    ) {
      site.operations.deleteUsedCustomColor(usedCustomColor, pageId);
    }
  });
};

const customColorsCleanUp = (store) => (next) => (action) => {
  const state = store.getState();

  switch (action.type) {
    case actionTypes.CLEAN_UP_CUSTOM_COLORS_FROM_ACTIVE_PAGE: {
      const activePageId = site.selectors.getActivePageId(state);

      const cleanUpBlocksRules = () => {
        const liveDocument = view.accessors.getLiveDocument();
        const blockList = getBlocksList(liveDocument);
        const styleSheet = getCustomTextColorsStylesheet();
        if (!styleSheet) return;

        Array.from(blockList).forEach((block) => {
          const customColorVars = getElementCustomColorVariables(block);

          if (customColorVars.length === 0) {
            removeBlockIdRuleFromStylesheet(styleSheet, block.id);
            return;
          }

          const blockColorsInStylesheet = getTextColorsUsedByBlock(block.id, styleSheet);
          blockColorsInStylesheet.forEach((blockColor) => {
            const isColorUsed = customColorVars.some((customColorVar) =>
              customColorVar.includes(blockColor)
            );

            if (!isColorUsed) {
              removeBlockIdPropertyFromStylesheet(styleSheet, block.id, blockColor);
            }
          });
        });

        updateCustomColorStylesheetInGlobal();
      };

      const manageCustomColors = () => {
        cleanUpUsedColors(state, activePageId);

        cleanUpBlocksRules();
      };

      manageCustomColors();

      next(action);
      break;
    }

    case site.actionTypes.DELETE_PAGE: {
      const { pageId } = action.payload;

      next(action);

      const cleanUpBlocksRules = (pageHTML) => {
        const document = parseDocumentFromString(pageHTML);
        const blockList = getBlocksList(document);
        const styleSheet = getCustomTextColorsStylesheet();
        if (!styleSheet) return;

        Array.from(blockList).forEach((block) =>
          removeBlockIdRuleFromStylesheet(styleSheet, block.id)
        );

        updateCustomColorStylesheetInGlobal();
      };

      const manageCustomColors = () => {
        cleanUpUsedColors(state, pageId, false);

        const page = site.selectors.getPage(state, pageId);

        if (page.html) {
          cleanUpBlocksRules(page.html);
        } else {
          site.helpers.fetchPageHTML(page.source).then(cleanUpBlocksRules);
        }
      };

      const isViewLoaded = view.selectors.getLoadedStatus(state);

      // we dont need clenup on initial site load
      if (isViewLoaded) {
        manageCustomColors();
      }

      break;
    }

    case view.actionTypes.DELETE_ELEMENT: {
      const { elementId, options } = action.payload;
      const { strategy } = options;
      const element = view.accessors.getLiveElement(elementId);

      const isElementBlock = blocks.verifiers.isElementBlock(element);
      const customColorVars = getElementCustomColorVariables(element);

      if (customColorVars.length) {
        const variables = template.selectors.getVariables(state);
        const { globalId } = variables[TEXT_COLOR_OPTIONS_VARIABLE][0];

        const newAction = {
          ...action,
          payload: {
            ...action.payload,
            globalsToUpdate: [
              ...action.payload.globalsToUpdate,
              {
                id: globalId,
              },
            ],
          },
        };

        next(newAction);

        const manageCustomColors = () => {
          const styleSheet = getCustomTextColorsStylesheet();
          if (!styleSheet) return;

          if (isElementBlock) {
            removeBlockIdRuleFromStylesheet(styleSheet, element.id);
            updateCustomColorStylesheetInGlobal();
          } else {
            const block = element.closest(BLOCK_SELECTOR);
            const blockCustomColorVars = getElementCustomColorVariables(block);
            const customColorVarsToRemove = customColorVars.filter(
              (customColorVar) =>
                blockCustomColorVars.filter(
                  (blockCustomColorVar) => blockCustomColorVar === customColorVar
                ).length === 1
            );

            if (customColorVarsToRemove.length) {
              customColorVarsToRemove.forEach((customColorVarToRemove) => {
                removeBlockIdPropertyFromStylesheet(styleSheet, block.id, customColorVarToRemove);
              });
              updateCustomColorStylesheetInGlobal();
            }
          }
        };

        if (strategy === UPDATE_STATIC_ONLY) {
          hooks.operations.subscribe(
            hooks.availableHooks.ON_LIVE_DOCUMENT_CONNECTED,
            manageCustomColors,
            { once: true }
          );
        } else {
          manageCustomColors();
        }

        break;
      }

      next(action);
      break;
    }

    case actionTypes.REMOVE_CUSTOM_COLORS_CSS_RULES_FOR_BLOCKS: {
      const { blocksIdsToRemove } = action.payload;
      const styleSheet = getCustomTextColorsStylesheet();

      if (styleSheet && blocksIdsToRemove.length) {
        blocksIdsToRemove.forEach((blockId) => {
          removeBlockIdRuleFromStylesheet(styleSheet, blockId);
        });
        updateCustomColorStylesheetInGlobal();
      }

      next(action);
      break;
    }

    case site.actionTypes.SET_PAGES: {
      const oldPages = site.selectors.getPages(state);
      const newPages = action.payload.pages;
      const newPagesIds = newPages.map((newPage) => newPage.id);

      const deletedOldPages = oldPages.filter((oldPage) => !newPagesIds.includes(oldPage.id));

      next(action);

      deletedOldPages.forEach((deletedOldPage) => {
        if (!deletedOldPage.usedCustomColors) return;

        deletedOldPage.usedCustomColors.forEach((usedCustomColor) => {
          site.operations.deleteUsedCustomColor(usedCustomColor);
        });
      });

      break;
    }

    case site.actionTypes.DELETE_USED_CUSTOM_COLOR: {
      const { color } = action.payload;

      next(action);

      if (!isCssColorVariableExist(color)) {
        const styleSheet = getCustomTextColorsStylesheet();
        const shouldRemoveCustomColor = !isCustomColorUsed(color);

        if (!styleSheet) break;

        cleanUpColorFromStylesheet(styleSheet, color, shouldRemoveCustomColor);
        updateCustomColorStylesheetInGlobal();
      }

      break;
    }

    default:
      next(action);
  }
};

export default customColorsCleanUp;
